import { getState, setState } from '../store';
import { wsUrl, getGameProgress, type TeamProgress, type GameProgress } from '../api/game-api';
import { ReconnectingWebSocket } from '../api/websocket';
import { navigate } from '../router';
import { showReconnecting, hideStatus } from '../components/connection-status';
import { createProgressCard, type ProgressData } from '../components/progress-card';
import { ReconcileGate } from '../utils/reconcile-gate';
import { createViewLifecycle, type ViewLifecycle } from '../utils/view-lifecycle';
import { installVisibilityReconcile } from '../utils/visibility-reconciler';

let ws: ReconnectingWebSocket | null = null;
// /investigate 2026-05-01 v0.79.20 (F-003) — iOS Safari suspends backgrounded
// tabs and kills the spectator WS. The 10s REST poll eventually catches up,
// but on resume the user stares at a stale board for up to ~10s. Mirror the
// lobby-view / location-view / chat-view reconcile contract: cold-mount,
// visibilitychange, pageshow each trigger an immediate `getGameProgress`
// fetch. `getGameProgress` already routes through `request()` which dispatches
// `aq:auth-permanent-failure` on 401/403, so stale-token handling is
// transitive — no explicit `isPermanentAuthFailure` call needed here.
//
// Plan B Task 5 (2026-05-11) — pollTimer, visibilityHandler, pageshowHandler
// are now owned by activeLc. The poll interval is registered via
// activeLc.setInterval; the visibility/pageshow listeners are installed via
// the visibility-reconciler foundation (see Step 3 of renderSpectatorView).
// All four resources tear down through activeLc.cleanup() in destroy.
let activeLc: ViewLifecycle | null = null;
// Reconcile gate — owns concurrent-collapse lock + 15s abort timeout. iOS
// Safari fires visibilitychange + pageshow nearly simultaneously on resume;
// without the lock the spectator board would render the same payload twice
// in a tick. Same shape as lobby/chat — see `pwa/src/utils/reconcile-gate.ts`.
const spectatorGate = new ReconcileGate();
// Module-level reference to the render closure so the reconcile fn can write
// into the live DOM without re-mounting the view. Cleared on destroy.
let applyTeams: ((teams: ProgressData[]) => void) | null = null;

/**
 * Map backend `TeamProgress` to the `ProgressData` shape `progress-card`
 * expects. Exported for tests — the field-name mapping is the reason the
 * spectator board stayed blank for every release before this fix (backend
 * `nickname`/`currentLocationIndex` vs PWA `teamName`/`locationsCompleted`).
 */
export function toProgressData(t: TeamProgress): ProgressData {
  return {
    teamName: t.nickname,
    locationsCompleted: t.currentLocationIndex,
    totalLocations: t.totalLocations,
    finished: t.finished,
  };
}

/**
 * /investigate 2026-05-01 v0.79.20 (F-003) — Spectator reconcile against
 * server snapshot.
 *
 * Same architectural class as session-89 captain-desync (location-view) and
 * the v0.79.16 lobby fix. WS = delta, REST = state. Cold-mount AND
 * visibility-resume must both pull a fresh snapshot. Without this, an
 * iOS-Safari spectator who backgrounds the tab during a captain's geo-verify
 * would see the stale «загадка» card until the lazy 10s poll fires.
 *
 * Returns void — the spectator view never navigates away from itself except
 * on GAME_FINISHED (WS) or the explicit Leave button. Errors are non-fatal;
 * the next 10s poll or WS reconnect will catch up. Auth-failure dispatch
 * runs transitively through `request()` inside `getGameProgress`.
 *
 * Exported for test access.
 */
export async function reconcileSpectatorAgainstServer(): Promise<void> {
  await spectatorGate.run<void>(undefined, undefined, async (signal) => {
    const state = getState();
    if (!state.gameCode || !state.playerId) return;
    // Route guard — bail if user navigated away mid-flight. Mirrors the
    // pattern in lobby-view (`#/lobby`) and chat-view (`#/chat`). Spectator
    // route hash is `#/spectator`. `(hash || '')` is defensive against test
    // harnesses that stub window.location with a partial object.
    const currentHash = typeof window === 'undefined' ? '' : (window.location.hash || '');
    if (typeof window !== 'undefined' && !currentHash.startsWith('#/spectator')) return;
    // Note: getGameProgress routes through request() in game-api.ts which
    // already dispatches `aq:auth-permanent-failure` on 401/403, so stale-token
    // handling is transitive — no explicit isPermanentAuthFailure call here.
    // /api/.../progress hits Workbox's 10s catch-all rule (not 5s /player-state),
    // so the 15s gate timeout fires AFTER Workbox would have completed.
    const progress = await getGameProgress(state.gameCode, state.playerId, signal);
    // Re-check route after await — the user could have tapped Leave while
    // the fetch was in flight.
    const hashAfter = typeof window === 'undefined' ? '' : (window.location.hash || '');
    if (typeof window !== 'undefined' && !hashAfter.startsWith('#/spectator')) return;
    if (!applyTeams) return;
    applyTeams((progress.teams ?? []).map(toProgressData));
  });
}

export function renderSpectatorView(container: HTMLElement): void {
  const state = getState();

  // Hero title is assigned via textContent BELOW (not interpolated into
  // innerHTML) — `state.gameTitle` is organizer-controlled and survives
  // reload via state-persistence.ts. Direct interpolation = stored XSS.
  // Mirrors the join-view.ts L362-396 pattern. See contract test in
  // pwa/tests/spectator-xss-contract.test.ts.
  container.innerHTML = `
    <div class="screen screen-enter screen-split">
      <div class="screen-content">
        <div style="text-align: center; margin-bottom: var(--sp-xl);">
          <div style="font-size: 28px; color: var(--amber); margin-bottom: var(--sp-sm);">👁</div>
          <p class="text-eyebrow" style="color: var(--amber); margin-bottom: var(--sp-sm);">НАБЛЮДАТЕЛЬ</p>
          <h1 class="text-display-large" id="spectator-hero-title"></h1>
        </div>
        <div class="spacer"></div>
      </div>

      <div class="screen-controls">
        <p class="text-eyebrow" style="margin-bottom: var(--sp-md);">ПРОГРЕСС КОМАНД</p>
        <div id="spectator-teams"></div>
        <div id="spectator-empty" class="text-body" style="color: var(--text-hint); text-align: center; padding: var(--sp-xl);">
          Пока нет участников
        </div>

        <button id="spectator-leave" class="btn btn-danger-outline btn-full" style="margin-top: var(--sp-lg);">
          Покинуть наблюдение
        </button>
      </div>
    </div>
  `;

  // textContent assignment — safe even if gameTitle contains HTML/JS.
  // The `||` fallback is server-generated 6-digit gameCode (also safe).
  const titleEl = container.querySelector<HTMLElement>('#spectator-hero-title')!;
  titleEl.textContent = state.gameTitle || `Игра ${state.gameCode}`;

  const teamsEl = container.querySelector<HTMLElement>('#spectator-teams')!;
  const emptyEl = container.querySelector<HTMLElement>('#spectator-empty')!;

  function renderTeams(teams: ProgressData[]): void {
    if (teams.length === 0) {
      emptyEl.style.display = 'block';
      teamsEl.innerHTML = '';
      return;
    }
    emptyEl.style.display = 'none';
    teamsEl.innerHTML = '';
    teams.forEach(t => teamsEl.appendChild(createProgressCard(t)));
  }

  // Stash the render closure on a module-level handle so reconcile can write
  // into the live DOM. Cleared on destroy.
  applyTeams = renderTeams;

  // WebSocket for spectator updates
  ws = new ReconnectingWebSocket(
    wsUrl(`/ws/games/${state.gameCode}/spectator?playerId=${state.playerId}`),
    (connected) => {
      setState({ wsConnected: connected });
      if (connected) hideStatus();
      else showReconnecting();
    }
  );

  ws.on('PROGRESS_UPDATE', (msg: unknown) => {
    // Backend sends SpectatorEvent { type, progress: { teams } } — read the
    // nested envelope, not msg.teams. Guard defensively so a misshapen
    // payload draws the empty state instead of crashing the handler
    // (ReconnectingWebSocket swallows handler errors as "WS parse error").
    const event = msg as { progress?: GameProgress };
    const teams = event.progress?.teams ?? [];
    renderTeams(teams.map(toProgressData));
  });

  ws.on('GAME_FINISHED', () => {
    setState({ status: 'FINISHED' });
    navigate('leaderboard');
  });

  ws.connect();

  // Cold-mount reconcile — fire-and-forget. The WS handshake also pushes an
  // initial PROGRESS_UPDATE on connect (see GameRoutes.kt spectator handler),
  // but on iOS Safari resume the WS may be DEAD with no observable signal,
  // and `ReconnectingWebSocket.scheduleReconnect` waits up to 30s with
  // exponential backoff. The REST pull renders within one round-trip.
  void reconcileSpectatorAgainstServer();

  // Plan B Task 5 (2026-05-11) — visibility/pageshow + poll lifecycle is
  // delegated to `activeLc`. The composite callback to
  // `installVisibilityReconcile` does BOTH the reconcile AND the Bug 2
  // forceReconnect (Option A from the BLOCKED escalation), so the
  // pre-extraction inline-handler shape is preserved structurally — both
  // resume paths (visibilitychange + pageshow) run the same recovery body.
  activeLc = createViewLifecycle();

  // Fallback REST polling — renders teams when the WS handshake fails or
  // the connection drops between pushes. `/progress` returns the same
  // GameProgress shape the WS emits (teams only, no status). Finish
  // detection stays on the WS GAME_FINISHED channel.
  activeLc.setInterval(async () => {
    try {
      const progress = await getGameProgress(state.gameCode, state.playerId);
      renderTeams((progress.teams ?? []).map(toProgressData));
    } catch { /* ignore */ }
  }, 10000);

  // iOS Safari WS-suspend resume — reconcile + Bug 2 forceReconnect.
  // /investigate 2026-05-02 — without `ws?.forceReconnect()`, iOS Safari
  // leaves the spectator WS in stuck-CONNECTING after bg→fg, banner
  // «Переподключение…» persists forever. The reconcile is HTTP-only;
  // GameProgress pushes silently never resume without the explicit kick.
  installVisibilityReconcile(activeLc, () => {
    void reconcileSpectatorAgainstServer();
    ws?.forceReconnect();
  }, '#/spectator');

  // Leave
  container.querySelector('#spectator-leave')!.addEventListener('click', () => {
    navigate('join');
  });
}

export function destroySpectatorView(): void {
  // Plan B Task 5 (2026-05-11) — pollTimer + visibility/pageshow listeners
  // are released via `activeLc.cleanup()`. The genuinely module-scoped bits
  // (ws, spectatorGate, applyTeams) stay in the explicit destroy below
  // because they're referenced by callbacks/closures outside the lc-managed
  // scope (WS message handlers mutate `applyTeams`; the gate is shared
  // across renders for inflight-recovery; ws.close needs to fire before
  // we drop the reference).
  activeLc?.cleanup();
  activeLc = null;

  if (ws) { ws.close(); ws = null; }
  // Drop the inflight lock if it's still held. Without this, a fetch that
  // was awaiting on destroy keeps the module-level lock until its `finally`
  // runs; the NEXT mount's first visibility/pageshow flip then bails with
  // the inflight short-circuit and silently skips the resume reconcile that
  // this whole audit exists to deliver. Mirrors chat-view destroyChatView
  // second-pass /review fix.
  spectatorGate.release();
  applyTeams = null;
  // /investigate 2026-05-04 v0.80.7 — sweep the connection-bar singleton on
  // teardown. See destroyLobbyView for the full rationale; symmetry with
  // chat-view destroyChatView:1077 which already does this.
  hideStatus();
}
